/**
 * Toucan Reads — what the browser calls each page. The joining of the app's
 * name and the page's is the platform's; this file is only about what each
 * route's page is called.
 */

import { documentTitle, listById, listLabel, required } from "toucan";
import { itemById, UNFILED_TITLE } from "./model.ts";
import { routeItemId, routeListId } from "./routes.ts";
import { store } from "./state.ts";
import type { Route } from "./routes.ts";

/** What every page's title opens with. */
const APP = "Toucan Reads";

/** The page's own name, or "" where it has none to add — the whole library. */
function pageName(route: Route): string {
  const itemId = routeItemId(route);
  if (itemId !== undefined) {
    // Usually an item page is only drawn for a record the store still has;
    // one that has just gone — folded into another save, or deleted — falls
    // back to the app's own name until the redirect finishes.
    return itemById(store(), itemId)?.title ?? "";
  }
  const listId = routeListId(route);
  if (listId === "") {
    return UNFILED_TITLE;
  }
  if (listId !== null) {
    // The shell shows the whole library for a list that has gone, so the
    // route only ever names one the store holds.
    return listLabel(required(listById(store().lists, listId), "list"));
  }
  if (route.name === "add") {
    return "Save a link";
  }
  if (route.name === "import") {
    return "Import articles";
  }
  if (route.name === "lists") {
    return "Manage lists";
  }
  if (route.name === "settings") {
    return "Settings";
  }
  return "";
}

/** What the browser should be calling the page this route shows. */
export function pageTitle(route: Route): string {
  return documentTitle(APP, pageName(route));
}
